import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {isEmpty, map} from 'lodash';
import React, {useEffect} from 'react';
import {useTranslation} from "react-i18next";
import {Link, Redirect, Route, Switch, useLocation} from "react-router-dom";
import {color, space, type} from "common/tokens";
import useFreshData, {dataKeys} from "common/useFreshData";
import HubSessionCard from "components/redesign/HubSessionCard";
import Skeleton from "components/redesign/Skeleton";
import FeedContainer from "containers/FeedContainer";

const useStyles = makeStyles(theme => ({
  page: {
    background: color.SURFACE,
    minHeight: '100%',
  },
  container: {
    width: '100%',
    maxWidth: space.CONTAINER_MAX,
    margin: '0 auto',
    padding: '40px 32px 64px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 28,
    [theme.breakpoints.down('md')]: {
      padding: '24px 16px 48px',
    },
  },
  title: {
    ...type.pageTitle,
    color: color.TEXT,
    margin: 0,
  },
  columns: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 280px)',
    gap: 32,
    alignItems: 'start',
    [theme.breakpoints.down('lg')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  sessions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    ...type.chip,
    borderRadius: 9999,
    border: `1px solid ${color.CARD_BORDER}`,
    background: color.SURFACE,
    color: color.TEXT_MUTED,
    padding: '8px 16px',
    textDecoration: 'none',
  },
  chipCurrent: {
    borderColor: 'transparent',
    background: color.ACCENT,
    color: color.ON_ACCENT,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(196px, 1fr))',
    gap: 16,
  },
  empty: {
    ...type.body,
    color: color.TEXT_MUTED,
    margin: '48px 0',
    textAlign: 'center',
  },
}));

const LearningHub = ({
  allSessions,
  mySessions,
  bookmarkedSessions,
  initSessions,
  initBookmarkedSessions,
  toggleBookmark,
  setCurrentPath,
}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const {search} = useLocation();

  useEffect(() => {
    setCurrentPath('learninghub');
  }, [setCurrentPath]);

  // Both lists start empty in the store, so a cold open cannot be told from a
  // programme with no sessions without waiting on the calls themselves.
  const sessionsSettled = useFreshData(dataKeys.SESSIONS, initSessions);
  const bookmarksSettled = useFreshData(dataKeys.BOOKMARKED_SESSIONS, initBookmarkedSessions);
  const loaded = sessionsSettled && bookmarksSettled;

  const current = new URLSearchParams(search).get('filter') || 'all';

  const lists = {
    all: [...(allSessions || []), ...(mySessions || [])],
    ongoing: mySessions,
    curated: allSessions,
    bookmarked: bookmarkedSessions,
  };

  const tabs = [
    {key: 'all', label: t('allSessions')},
    {key: 'ongoing', label: t('ongoingSessions')},
    {key: 'curated', label: t('curatedSessions')},
    {key: 'bookmarked', label: t('bookmarkedSessions')},
  ];

  const sessions = lists[current] || lists.all;

  const home = (
    <div className={`${classes.container} ui-enter`}>
      <h1 className={classes.title}>{t('learningHub')}</h1>

      <div className={classes.columns}>
        <div className={classes.sessions}>
          <div className={classes.chips}>
            {map(tabs, tab => (
              <Link
                key={tab.key}
                to={`/learninghub?filter=${tab.key}`}
                className={classNames(classes.chip, current === tab.key && classes.chipCurrent)}
              >
                {tab.label}
              </Link>
            ))}
          </div>

          {!loaded ? (
            <div className={classes.grid}>
              <Skeleton height={200}/>
              <Skeleton height={200}/>
              <Skeleton height={200}/>
              <Skeleton height={200}/>
            </div>
          ) : isEmpty(sessions) ? (
            <p className={classes.empty}>{t('noSessionsYet')}</p>
          ) : (
            <div className={classes.grid}>
              {map(sessions, session => (
                <HubSessionCard key={session.id} session={session} toggleBookmark={toggleBookmark}/>
              ))}
            </div>
          )}
        </div>

        <FeedContainer/>
      </div>
    </div>
  );

  return (
    <div className={classes.page}>
      <Switch>
        {/* The lists used to live on a page each; those links now land on the
            hub with the matching filter already on. */}
        <Redirect exact from={'/learninghub/allsession'} to={'/learninghub?filter=all'}/>
        <Redirect exact from={'/learninghub/ongoingsessions'} to={'/learninghub?filter=ongoing'}/>
        <Redirect exact from={'/learninghub/curatedsession'} to={'/learninghub?filter=curated'}/>
        <Redirect exact from={'/learninghub/bookmarkedsessions'} to={'/learninghub?filter=bookmarked'}/>
        <Route path={'/learninghub'}>{home}</Route>
      </Switch>
    </div>
  );
};

export default LearningHub;
